import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Box, rem, Spinner, VStack } from '@bonusly/design-system';

import { LeaderboardFilters as LeaderboardFiltersType, useInfiniteLeaderboard } from '../../../bonus-feed/features/recognition/api/hooks/leaderboards';
import { useTimeframeDates } from './hooks/use-timeframe';
import { LeaderboardEmptyState } from './leaderboard-empty-state';
import { LeaderboardUserRow, LeaderboardUserRowSkeleton } from './leaderboard-user-row';
import { LeaderboardState } from './types';

export interface LeaderboardExportData {
	totalCount: number;
	filters: Omit<LeaderboardFiltersType, 'limit' | 'offset'>;
	isReady: boolean;
	isFetching: boolean;
}

interface LeaderboardDetailViewProps {
	state: LeaderboardState;
	onExportDataChange?: (data: LeaderboardExportData) => void;
}

const INITIAL_LIMIT = 15;
const LOAD_MORE_LIMIT = 10;

// Helper to build scope filter params from state
const getScopeParams = (state: LeaderboardState) => {
	if (state.scopeType === 'team' && state.selectedTeam) {
		return { team: state.selectedTeam };
	}
	if (state.scopeType === 'department' && state.selectedDepartment) {
		return { department: state.selectedDepartment };
	}
	return {};
};

export const LeaderboardDetailView: FC<LeaderboardDetailViewProps> = ({ state, onExportDataChange }) => {
	const { startDate, endDate } = useTimeframeDates(state.selectedTimeframe, state.customStartDate, state.customEndDate);
	const loadMoreRef = useRef<HTMLDivElement>(null);

	// Create a stable filter key to detect when filters change
	const filterKey = useMemo(
		() =>
			JSON.stringify({
				startDate,
				endDate,
				hashtag: state.selectedHashtag,
				direction: state.direction,
				scopeType: state.scopeType,
				team: state.selectedTeam,
				department: state.selectedDepartment,
			}),
		[startDate, endDate, state.selectedHashtag, state.direction, state.scopeType, state.selectedTeam, state.selectedDepartment]
	);

	// Track the previous filter key to detect changes
	const [activeFilterKey, setActiveFilterKey] = useState(filterKey);
	const filtersChanged = filterKey !== activeFilterKey;

	// Memoize filter object to prevent unnecessary API refetches
	const filters = useMemo(
		() => ({
			start_date: startDate,
			end_date: endDate,
			direction: state.direction,
			...(state.selectedHashtag && { hashtag: state.selectedHashtag }),
			...getScopeParams(state),
		}),
		[startDate, endDate, state.direction, state.selectedHashtag, state.scopeType, state.selectedTeam, state.selectedDepartment]
	);

	const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteLeaderboard(filters, {
		initialLimit: INITIAL_LIMIT,
		loadMoreLimit: LOAD_MORE_LIMIT,
	});

	// Update activeFilterKey once data has loaded for the new filters
	useEffect(() => {
		if (!isFetching && filterKey !== activeFilterKey) {
			setActiveFilterKey(filterKey);
		}
	}, [isFetching, filterKey, activeFilterKey]);

	const users = data?.users ?? [];
	const totalCount = data?.totalCount ?? 0;
	// Show skeleton when loading OR when filters changed and we're fetching new data
	const showInitialSkeleton = isLoading || filtersChanged || (isFetching && users.length === 0);

	// Notify parent of export-relevant data only when values actually change
	const isReady = !showInitialSkeleton && totalCount > 0;
	const exportDataKey = `${totalCount}-${isReady}-${isFetching}-${filterKey}`;
	const prevExportDataKeyRef = useRef('');
	useEffect(() => {
		if (prevExportDataKeyRef.current !== exportDataKey) {
			prevExportDataKeyRef.current = exportDataKey;
			onExportDataChange?.({ totalCount, filters, isReady, isFetching });
		}
	}, [exportDataKey, totalCount, filters, isReady, isFetching, onExportDataChange]);

	// Scroll-based infinite loading - attach to the panel's scroll container
	useEffect(() => {
		// Don't set up scroll listener while showing skeleton
		if (showInitialSkeleton) return;

		const element = loadMoreRef.current;
		if (!element) return;

		// Find the panel scroll container by looking for the element with overflowY auto/scroll
		let scrollContainer: HTMLElement | null = element.parentElement;
		while (scrollContainer) {
			const style = getComputedStyle(scrollContainer);
			const overflow = style.overflowY;
			if (overflow === 'auto' || overflow === 'scroll') {
				break;
			}
			scrollContainer = scrollContainer.parentElement;
		}

		if (!scrollContainer) return;

		const handleScroll = () => {
			if (isFetchingNextPage || !hasNextPage) return;
			if (!scrollContainer) return;

			const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
			// Load more when within 150px of the bottom
			if (scrollHeight - scrollTop - clientHeight < 150) {
				fetchNextPage();
			}
		};

		// Check immediately in case we're already at the bottom
		handleScroll();

		scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
		return () => scrollContainer?.removeEventListener('scroll', handleScroll);
	}, [hasNextPage, isFetchingNextPage, fetchNextPage, users.length, showInitialSkeleton, activeFilterKey]);

	return (
		<VStack spacing={0} align="stretch" px={rem(12)}>
			{/* User list */}
			<Box mt={rem(16)}>
				{showInitialSkeleton ? (
					<VStack as="ul" spacing={0} align="stretch" listStyleType="none" m={0} p={0}>
						{Array.from({ length: 10 }).map((_, i) => (
							<LeaderboardUserRowSkeleton key={i} />
						))}
					</VStack>
				) : users.length > 0 ? (
					<>
						<VStack as="ul" spacing={0} align="stretch" listStyleType="none" m={0} p={0}>
							{users.map((user, index) => (
								<LeaderboardUserRow
									key={`${activeFilterKey}-${user.id}-${index}`}
									record={user}
									rank={index + 1}
									hashtag={state.selectedHashtag ? `#${state.selectedHashtag}` : null}
									direction={state.direction}
								/>
							))}
						</VStack>

						{/* Infinite scroll trigger / loading indicator */}
						<Box ref={loadMoreRef} py={rem(12)} textAlign="center">
							{isFetchingNextPage && <Spinner size="sm" color="primary.fg" />}
						</Box>
					</>
				) : (
					<LeaderboardEmptyState />
				)}
			</Box>
		</VStack>
	);
};
